import { convexQuery } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { useTable } from '@tanstack/react-table'
import type { FunctionReturnType } from 'convex/server'
import { IconCubes2Outline18, IconXmarkOutline18 } from 'nucleo-ui-outline-18'
import { useMemo } from 'react'
import {
  CatalogCompleteness,
  CatalogEnrichmentPill,
  CatalogSourceDots,
  CatalogStatusPill,
  CatalogThumb,
} from '~/components/admin/catalog-bits'
import { StatBox } from '~/components/admin/detail-bits'
import { Button } from '~/components/ui/button'
import { DataTable, type DataTableFeatures, dataTableFeatures } from '~/components/ui/data-table'
import { EmptyState } from '~/components/ui/empty-state'
import { Label } from '~/components/ui/label'
import { PageHeader } from '~/components/ui/page-header'
import { SearchField } from '~/components/ui/search-field'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { Switch } from '~/components/ui/switch'
import { useDebouncedValue } from '~/hooks/use-debounced-value'
import { useGrowingPage } from '~/hooks/use-growing-page'
import {
  CATALOG_CATEGORY_ORDER,
  CATALOG_ENRICHMENT_ORDER,
  CATALOG_STATUS_ORDER,
  catalogCategoryLabel,
  catalogEnrichmentLabel,
  catalogStatusLabel,
  isCatalogCategory,
  isCatalogEnrichment,
  isCatalogStatus,
} from '~/lib/catalog-labels'
import { formatRelativeTime } from '~/lib/datetime'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'
import type {
  CatalogCategory,
  CatalogEnrichment,
  CatalogProductStatus,
} from '~convex/lib/catalogEnums'

/**
 * `/admin/catalog` — the global product catalog, browsed. Filters live in
 * the URL so a brand link from a product page, or a stat tile, lands on a
 * filtered list that can be shared.
 */
type CatalogSearch = {
  brandId?: Id<'catalogBrands'>
  category?: CatalogCategory
  /** Products whose projection points at this collection (from the collections page). */
  collection?: Id<'catalogCollections'>
  enrichment?: CatalogEnrichment
  merged?: boolean
  /** Only products first seen in the last N days — the "novidades" view. */
  new?: number
  q?: string
  status?: CatalogProductStatus
}

/** The "new" window the toggle uses; anything else in the URL is kept as typed. */
const NEW_WITHIN_DAYS = 30

const isBrandId = (value: unknown): value is Id<'catalogBrands'> =>
  typeof value === 'string' && value !== ''
const isCollectionId = (value: unknown): value is Id<'catalogCollections'> =>
  typeof value === 'string' && value !== ''
const isDayCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 && value <= 365

export const Route = createFileRoute('/admin/catalog/')({
  component: AdminCatalog,
  head: () => ({ meta: [{ title: titleFor(msg`Product catalog`) }] }),
  validateSearch: (search): CatalogSearch => ({
    ...(isBrandId(search.brandId) ? { brandId: search.brandId } : {}),
    ...(isCatalogCategory(search.category) ? { category: search.category } : {}),
    ...(isCollectionId(search.collection) ? { collection: search.collection } : {}),
    ...(isCatalogEnrichment(search.enrichment) ? { enrichment: search.enrichment } : {}),
    ...(search.merged === true ? { merged: true } : {}),
    ...(isDayCount(search.new) ? { new: search.new } : {}),
    ...(typeof search.q === 'string' && search.q !== '' ? { q: search.q } : {}),
    ...(isCatalogStatus(search.status) ? { status: search.status } : {}),
  }),
})

const PAGE_SIZE = 100
const ALL = '__all__'

type ProductRow = FunctionReturnType<typeof api.catalogAdminQueries.listProducts>['page'][number]
const EMPTY_ROWS: ProductRow[] = []

type BrandOption = FunctionReturnType<typeof api.catalogAdminQueries.listBrands>[number]

/**
 * The filter row. Every `Select` carries an `items` map built from the same
 * label source the options render — without it the trigger shows the raw
 * enum value.
 */
const CatalogFilterBar = ({
  brands,
  onChange,
  search,
}: {
  brands: BrandOption[]
  onChange: (patch: Partial<CatalogSearch>) => void
  search: CatalogSearch
}) => {
  const { t, i18n } = useLingui()
  const categoryItems: Record<string, string> = { [ALL]: t`All categories` }
  for (const category of CATALOG_CATEGORY_ORDER) {
    categoryItems[category] = catalogCategoryLabel(i18n, category)
  }
  const statusItems: Record<string, string> = { [ALL]: t`All statuses` }
  for (const status of CATALOG_STATUS_ORDER) {
    statusItems[status] = catalogStatusLabel(i18n, status)
  }
  const enrichmentItems: Record<string, string> = { [ALL]: t`Any enrichment` }
  for (const enrichment of CATALOG_ENRICHMENT_ORDER) {
    enrichmentItems[enrichment] = catalogEnrichmentLabel(i18n, enrichment)
  }
  const brandItems: Record<string, string> = { [ALL]: t`All brands` }
  for (const brand of brands) {
    brandItems[brand._id] = brand.name
  }
  return (
    <div className="flex flex-wrap items-center gap-2 px-5 pb-3">
      <Select
        items={categoryItems}
        onValueChange={(value) =>
          onChange({ category: isCatalogCategory(value) ? value : undefined })
        }
        value={search.category ?? ALL}
      >
        <SelectTrigger aria-label={t`Category`} className="min-w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{categoryItems[ALL]}</SelectItem>
          {CATALOG_CATEGORY_ORDER.map((category) => (
            <SelectItem key={category} value={category}>
              {categoryItems[category]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        items={brandItems}
        onValueChange={(value) =>
          onChange({ brandId: isBrandId(value) && value !== ALL ? value : undefined })
        }
        value={search.brandId ?? ALL}
      >
        {/* Capped as well as floored: brand names come from ingested data, so
            one long one would otherwise stretch the filter row arbitrarily. */}
        <SelectTrigger aria-label={t`Brand`} className="min-w-44 max-w-64">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{brandItems[ALL]}</SelectItem>
          {brands.map((brand) => (
            <SelectItem key={brand._id} value={brand._id}>
              {brand.name}{' '}
              <span className="text-muted-foreground tabular-nums">
                {i18n.number(brand.productCount)}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        items={statusItems}
        onValueChange={(value) => onChange({ status: isCatalogStatus(value) ? value : undefined })}
        value={search.status ?? ALL}
      >
        <SelectTrigger aria-label={t`Status`} className="min-w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{statusItems[ALL]}</SelectItem>
          {CATALOG_STATUS_ORDER.map((status) => (
            <SelectItem key={status} value={status}>
              {statusItems[status]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        items={enrichmentItems}
        onValueChange={(value) =>
          onChange({ enrichment: isCatalogEnrichment(value) ? value : undefined })
        }
        value={search.enrichment ?? ALL}
      >
        {/* `min-w`, not a fixed width: the base trigger is `w-fit`, and pinning
            it truncates any label longer than the box. "Any enrichment" fits at
            w-40, "Qualquer enriquecimento" does not — a fixed width here is a
            bet on one locale's string lengths. */}
        <SelectTrigger aria-label={t`Enrichment`} className="min-w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{enrichmentItems[ALL]}</SelectItem>
          {CATALOG_ENRICHMENT_ORDER.map((enrichment) => (
            <SelectItem key={enrichment} value={enrichment}>
              {enrichmentItems[enrichment]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Label className="ml-1 flex items-center gap-2 text-muted-foreground text-xs">
        <Switch
          checked={search.new !== undefined}
          onCheckedChange={(checked) => onChange({ new: checked ? NEW_WITHIN_DAYS : undefined })}
        />
        <Trans>New ({search.new ?? NEW_WITHIN_DAYS} d)</Trans>
      </Label>
      <Label className="ml-1 flex items-center gap-2 text-muted-foreground text-xs">
        <Switch
          checked={search.merged === true}
          onCheckedChange={(checked) => onChange({ merged: checked })}
        />
        <Trans>Show merged</Trans>
      </Label>
      {search.collection === undefined ? null : (
        <Button
          onClick={() => onChange({ collection: undefined })}
          size="xs"
          type="button"
          variant="secondary"
        >
          <Trans>Collection</Trans>
          <IconXmarkOutline18 />
        </Button>
      )}
    </div>
  )
}

/** The stat strip above the table; counts are floors past the scan cap. */
const CatalogStatStrip = ({
  stats,
}: {
  stats: FunctionReturnType<typeof api.catalogAdminQueries.catalogStats> | undefined
}) => {
  const { t, i18n } = useLingui()
  if (stats === undefined) {
    return null
  }
  return (
    <div className="grid grid-cols-2 gap-3 px-2 py-4 sm:grid-cols-4 lg:grid-cols-6">
      <StatBox label={t`Products`} value={i18n.number(stats.productCount)} />
      <StatBox label={t`Brands`} value={i18n.number(stats.brandCount)} />
      <StatBox
        label={t`Active · discontinued · hidden`}
        value={`${i18n.number(stats.byStatus.active)} · ${i18n.number(stats.byStatus.discontinued)} · ${i18n.number(stats.byStatus.hidden)}`}
      />
      <StatBox
        label={t`Thin · partial · rich`}
        value={`${i18n.number(stats.byEnrichment.thin)} · ${i18n.number(stats.byEnrichment.partial)} · ${i18n.number(stats.byEnrichment.rich)}`}
      />
      <StatBox label={t`Merged`} value={i18n.number(stats.merged)} />
      <StatBox
        label={t`Last batch`}
        value={
          stats.lastBatch === null
            ? '—'
            : `${formatRelativeTime(stats.lastBatch.receivedAt, i18n)} · ${stats.lastBatch.status === 'accepted' ? t`accepted` : t`rejected`}`
        }
      />
    </div>
  )
}

function AdminCatalog() {
  const { t, i18n } = useLingui()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const setSearch = (patch: Partial<CatalogSearch>) =>
    navigate({
      replace: true,
      search: (previous: CatalogSearch) => {
        // Rebuilt from entries rather than deleted by asserted key: an empty
        // value is dropped so it never reaches the URL.
        const pruned: CatalogSearch = {}
        for (const [key, value] of Object.entries({ ...previous, ...patch })) {
          if (value !== undefined && value !== false && value !== '') {
            Object.assign(pruned, { [key]: value })
          }
        }
        return pruned
      },
      to: '/admin/catalog',
    })

  const debouncedQ = useDebouncedValue(search.q?.trim() ?? '')
  const { paginationOpts, loadMore } = useGrowingPage(PAGE_SIZE)
  const { data, isPending } = useQuery(
    convexQuery(api.catalogAdminQueries.listProducts, {
      brandId: search.brandId,
      category: search.category,
      collectionId: search.collection,
      enrichment: search.enrichment,
      includeMerged: search.merged,
      newWithinDays: search.new,
      paginationOpts,
      search: debouncedQ === '' ? undefined : debouncedQ,
      status: search.status,
    })
  )
  const { data: stats } = useQuery(convexQuery(api.catalogAdminQueries.catalogStats, {}))
  const { data: brands } = useQuery(convexQuery(api.catalogAdminQueries.listBrands, {}))

  const columns = useMemo<ColumnDef<DataTableFeatures, ProductRow>[]>(
    () => [
      {
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-3">
            <CatalogThumb alt="" url={row.original.thumbUrl} />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{row.original.name}</span>
              <span className="truncate text-muted-foreground text-xs">
                {row.original.brandName}
                {row.original.mergedIntoId === null ? null : (
                  <>
                    {' · '}
                    <Trans>merged</Trans>
                  </>
                )}
              </span>
            </div>
          </div>
        ),
        enableSorting: false,
        header: () => t`Product`,
        id: 'product',
      },
      {
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {catalogCategoryLabel(i18n, row.original.category)}
            {row.original.subcategory === null ? '' : ` · ${row.original.subcategory}`}
          </span>
        ),
        enableSorting: false,
        header: () => t`Category`,
        id: 'category',
        meta: { className: 'hidden md:table-cell' },
      },
      {
        cell: ({ row }) =>
          row.original.collectionName === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="truncate">{row.original.collectionName}</span>
          ),
        enableSorting: false,
        header: () => t`Collection`,
        id: 'collection',
        meta: { className: 'hidden xl:table-cell' },
      },
      {
        cell: ({ row }) => <CatalogStatusPill status={row.original.status} />,
        enableSorting: false,
        header: () => t`Status`,
        id: 'status',
      },
      {
        cell: ({ row }) => <CatalogEnrichmentPill enrichment={row.original.enrichment} />,
        enableSorting: false,
        header: () => t`Enrichment`,
        id: 'enrichment',
        meta: { className: 'hidden sm:table-cell' },
      },
      {
        cell: ({ row }) => <CatalogCompleteness value={row.original.completeness} />,
        enableSorting: false,
        header: () => t`Completeness`,
        id: 'completeness',
        meta: { className: 'hidden lg:table-cell' },
      },
      {
        cell: ({ row }) => i18n.number(row.original.imageCount),
        enableSorting: false,
        header: () => t`Images`,
        id: 'images',
        meta: { className: 'hidden text-muted-foreground tabular-nums lg:table-cell' },
      },
      {
        cell: ({ row }) => <CatalogSourceDots sources={row.original.sources} />,
        enableSorting: false,
        header: () => t`Sources`,
        id: 'sources',
        meta: { className: 'hidden md:table-cell' },
      },
      {
        cell: ({ row }) => formatRelativeTime(row.original.lastSeenAt, i18n),
        enableSorting: false,
        header: () => t`Last seen`,
        id: 'lastSeenAt',
        meta: { className: 'hidden text-muted-foreground xl:table-cell' },
      },
    ],
    [t, i18n]
  )

  const rows = data?.page ?? EMPTY_ROWS
  const table = useTable({
    columns,
    data: rows,
    features: dataTableFeatures,
  })

  const filtering =
    debouncedQ !== '' ||
    search.brandId !== undefined ||
    search.category !== undefined ||
    search.status !== undefined ||
    search.enrichment !== undefined ||
    search.collection !== undefined ||
    search.new !== undefined

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        below={<CatalogFilterBar brands={brands ?? []} onChange={setSearch} search={search} />}
        count={stats?.productCount}
        title={t`Product catalog`}
      >
        <Button render={<Link to="/admin/catalog/brands" />} size="sm" variant="outline">
          <Trans>Brands</Trans>
        </Button>
        <Button render={<Link to="/admin/catalog/collections" />} size="sm" variant="outline">
          <Trans>Collections</Trans>
        </Button>
        <Button render={<Link to="/admin/catalog/imports" />} size="sm" variant="outline">
          <Trans>Import log</Trans>
        </Button>
        <SearchField onChange={(value) => setSearch({ q: value })} value={search.q ?? ''} />
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        <CatalogStatStrip stats={stats} />

        <DataTable
          empty={
            <EmptyState
              className="animation-duration-30 animate-fade-up"
              description={
                filtering
                  ? t`Try another search or loosen the filters.`
                  : t`Products arrive through the import endpoint; nothing has been posted yet.`
              }
              icon={IconCubes2Outline18}
              title={filtering ? t`No products found` : t`The catalog is empty`}
            />
          }
          isPending={isPending}
          onRowClick={(row) =>
            navigate({ params: { productId: row._id }, to: '/admin/catalog/$productId' })
          }
          table={table}
        />
        {data !== undefined && !data.isDone ? (
          <div className="flex justify-center py-3">
            <Button onClick={loadMore} size="sm" type="button" variant="outline">
              <Trans>Load more</Trans>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
